import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, KeyRound } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ACTION_LABEL, PracticeActionDialog } from './PracticeActionDialog';

const ACTIONS = { pending_approval: ['approve', 'reject'], approved: ['suspend'], suspended: ['reactivate'], rejected: ['approve'] };
const isPending = (o) => o.status === 'pending_approval' || o.status === 'pending';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU') : '—');
const ring = (o, hi) => (o.id === hi ? 'ring-2 ring-primary bg-primary/5' : '');

const ActionButtons = ({ o, onAct }) => (ACTIONS[isPending(o) ? 'pending_approval' : o.status] || []).map((a) => (
  <Button key={a} size="sm" variant={a === 'approve' || a === 'reactivate' ? 'default' : 'outline'} onClick={() => onAct(o, a)} data-testid={`platform-practice-${a}-${o.id}`}>{ACTION_LABEL[a]}</Button>
));

const Verified = ({ o }) => (
  <span className={o.owner_email_verified ? 'text-muted-foreground' : 'text-amber-700 font-medium'} data-testid={`platform-practice-verified-${o.id}`}>
    {o.owner_email_verified ? 'Email confirmed' : 'Email not confirmed'}</span>
);

export const MfaBanner = ({ onOpenSecurity }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="platform-mfa-banner">
    <KeyRound className="h-4 w-4" /><span className="flex-1">Set up your authenticator app in My security to approve practices</span>
    <Button size="sm" onClick={onOpenSecurity} data-testid="platform-mfa-banner-open-security">Open My security</Button>
  </div>
);

const PendingSection = ({ rows, hi, onAct }) => (
  <Card data-testid="platform-pending-approvals"><CardHeader><CardTitle className="flex items-center gap-2">Pending approvals
    <Badge variant="outline" data-testid="platform-pending-count">{rows.length}</Badge></CardTitle></CardHeader>
    <CardContent className="space-y-2">
      {rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="platform-pending-empty">No practices awaiting approval</p> : rows.map((o) => (
        <div key={o.id} id={`practice-row-${o.id}`} className={`flex flex-wrap items-center gap-3 rounded-md border p-3 ${ring(o, hi)}`} data-testid={`platform-pending-${o.id}`}>
          <div className="min-w-0 flex-1 text-sm">
            <div className="font-medium">{o.name} <span className="font-mono text-xs text-muted-foreground">{o.slug}</span></div>
            <div className="text-xs">{o.owner_email || 'No owner'} · <Verified o={o} /> · signed up {fmtDate(o.created_at)}</div>
          </div>
          <div className="flex shrink-0 gap-2"><ActionButtons o={o} onAct={onAct} /></div>
        </div>))}
    </CardContent></Card>
);

const AllTable = ({ rows, hi, onAct }) => (
  <Card><CardHeader><CardTitle>All practices</CardTitle>
    <CardDescription>Metadata only – no tenant data. Every action re-confirms your password + code and is audited.</CardDescription></CardHeader>
    <CardContent className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="platform-practices-table">
        <thead className="text-xs text-muted-foreground"><tr>{['Practice', 'Actions', 'Status', 'Owner', 'Created', 'Users', 'Clients'].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
        <tbody>{rows.map((o) => (
          <tr key={o.id} id={isPending(o) ? undefined : `practice-row-${o.id}`} className={`border-t ${ring(o, hi)}`} data-testid={`platform-practice-${o.id}`}>
            <td className="p-2"><div className="font-medium">{o.name}</div><div className="font-mono text-xs text-muted-foreground">{o.slug}</div></td>
            <td className="p-2"><div className="flex gap-1 whitespace-nowrap"><ActionButtons o={o} onAct={onAct} /></div></td>
            <td className="p-2"><Badge variant={o.status === 'approved' ? 'default' : 'outline'} data-testid={`platform-practice-status-${o.id}`}>{o.status.replace('_', ' ')}</Badge>
              {o.status_reason && <span className="block text-xs text-muted-foreground">{o.status_reason}</span>}</td>
            <td className="p-2 text-xs">{o.owner_email || '—'}<span className="block"><Verified o={o} /></span></td>
            <td className="p-2 text-xs">{fmtDate(o.created_at)}</td><td className="p-2">{o.user_count}</td><td className="p-2">{o.client_count}</td>
          </tr>))}</tbody>
      </table>
    </CardContent></Card>
);

export const PracticesCard = ({ highlightId, onCount, reloadKey, mfaMissing, onOpenSecurity }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [target, setTarget] = useState(null);
  const load = useCallback(() => api.get('/api/platform/practices').then((r) => {
    setRows(r.data); setError(null); onCount?.(r.data.filter(isPending).length);
  }).catch((e) => { setRows(null); setError(e.response?.data?.detail || e.message || 'Failed to load practices'); }), [onCount]);
  useEffect(() => { load(); }, [load, reloadKey]);
  useEffect(() => {
    if (rows && highlightId) document.getElementById(`practice-row-${highlightId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [rows, highlightId]);
  const mfaBlocked = mfaMissing || error === 'MFA_SETUP_REQUIRED';
  return (
    <div className="space-y-4" data-testid="platform-practices">
      {mfaBlocked && <MfaBanner onOpenSecurity={onOpenSecurity} />}
      {error && error !== 'MFA_SETUP_REQUIRED' && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive" role="alert" data-testid="platform-practices-error">
          <AlertTriangle className="h-4 w-4" /><span className="flex-1">Couldn&apos;t load practices: {String(error)}</span>
          <Button size="sm" variant="outline" onClick={load} data-testid="platform-practices-retry">Try again</Button>
        </div>)}
      {rows === null && !error && <p className="text-sm text-muted-foreground" data-testid="platform-practices-loading">Loading practices…</p>}
      {rows && <>
        <PendingSection rows={rows.filter(isPending)} hi={highlightId} onAct={(o, action) => setTarget({ o, action })} />
        <AllTable rows={rows} hi={highlightId} onAct={(o, action) => setTarget({ o, action })} />
      </>}
      <PracticeActionDialog target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
    </div>
  );
};
